import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Configuration, AspectTextOverviewViewModel, AspectTextService } from '../../index';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';
import { AspectTextDetailsComponent } from '../aspect-text-details/aspect-text-details.component';

@Component({
    selector: 'app-aspect-text-list',
    templateUrl: './aspect-text-list.component.html',
    styleUrls: ['./aspect-text-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AspectTextListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;
    @Input() aspectId: number = null;
    @Input() aspectName: String = null;

    displayedColumns = ['aspectTextId', 'name', 'versionNo', 'enabled', 'bedAndBreakfast', 'gastgiveri', 'hotell', 'hus', 'lagenhet', 'pensionat', 'restaurang', 'stuga', 'upplevelse', 'vandrarhem', 'actions'];
    dataSource: MatTableDataSource<AspectTextOverviewViewModel>;
    loadingIndicator: boolean;

    // Filtering
    @Input() showTitle: boolean = true;
    @Input() fixedView: boolean = false;
    currentSearchText: string;

    constructor(private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private aspectService: AspectTextService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        this.dataSource = new MatTableDataSource<AspectTextOverviewViewModel>();
        this.currentSearchText = "";

    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.aspectService.getAspectTextsByAspect(this.aspectId).subscribe(
            bus => this.onDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(aspects: AspectTextOverviewViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        var mappedAspectTexts = aspects.map(t => new AspectTextInfo(t));
        this.dataSource.data = mappedAspectTexts;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    confirmDelete(aspect: AspectTextOverviewViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            aspect.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.aspectService.deleteAspectText(aspect.aspectTextId)
                    .subscribe(results => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;
                        this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                            aspect.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                            MessageSeverity.success);
                        this.loadData();
                    },
                    error => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;

                        let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                            ": " +
                            Utilities.getHttpResponseMessage(error);

                        this.alertService.showStickyMessage(
                            this.translationService.getTranslation("globalTexts.DeleteError"),
                            errorMessage,
                            MessageSeverity.error,
                            error);
                    });
            });
    }

    public showDetails(event: Event, aspectText: AspectTextInfo): void {
        let config = new MatDialogConfig();
        config.data = {
            aspectTextId: aspectText == null ? null : aspectText.aspectTextId,
            aspectId: this.aspectId,
            aspectName: this.aspectName
        };
        config.minWidth = "700px";
        config.disableClose = true;
        event.stopPropagation();

        let dialogRef = this.dialog.open(AspectTextDetailsComponent, config);
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadData();
            }
        });
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }
}

export class AspectTextInfo {
    constructor(aspectTextOverview: AspectTextOverviewViewModel) {
        this.aspectId = aspectTextOverview.aspectId;
        this.aspectTextId = aspectTextOverview.aspectTextId;
        this.bedAndBreakfast = aspectTextOverview.businessCategories.includes(1);
        this.createdBy = aspectTextOverview.createdBy;
        this.createdDate = aspectTextOverview.createdDate;
        this.enabled = aspectTextOverview.enabled;
        this.gastgiveri = aspectTextOverview.businessCategories.includes(2);
        this.hotell = aspectTextOverview.businessCategories.includes(3);
        this.hus = aspectTextOverview.businessCategories.includes(4);
        this.lagenhet = aspectTextOverview.businessCategories.includes(5);
        this.name = aspectTextOverview.name;
        this.pensionat = aspectTextOverview.businessCategories.includes(6);
        this.restaurang = aspectTextOverview.businessCategories.includes(7);
        this.sortOrder = aspectTextOverview.sortOrder;
        this.stuga = aspectTextOverview.businessCategories.includes(8);
        this.updatedBy = aspectTextOverview.updatedBy;
        this.updatedDate = aspectTextOverview.updatedDate;
        this.upplevelse = aspectTextOverview.businessCategories.includes(9);
        this.vandrarhem = aspectTextOverview.businessCategories.includes(10);
        this.versionNo = aspectTextOverview.versionNo;
    }

    aspectTextId?: number;
    name?: string;
    enabled?: boolean;
    aspectId?: number;
    sortOrder?: number;
    versionNo?: number;
    createdBy?: string;
    createdDate?: string;
    updatedBy?: string;
    updatedDate?: string;

    bedAndBreakfast: boolean;
    gastgiveri: boolean;
    hotell: boolean;
    hus: boolean;
    lagenhet: boolean;
    pensionat: boolean;
    restaurang: boolean;
    stuga: boolean;
    upplevelse: boolean;
    vandrarhem: boolean;
}
